'use client'

import { faSpinner } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button, Checkbox } from '@material-tailwind/react'
import { RootState } from 'features/store'
import { Field, Form, Formik } from 'formik'
import {
  useGetNotificationPreferencesQuery,
  useUpsertNotificationPreferencesMutation,
  useUpdateProfileMutation,
} from 'graphql/generated/graphql'
import { Notification } from 'iconsax-react'
import React, { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { setUser } from 'features/authSlice'
import { toast } from 'react-toastify'
import { ProfileSettingsOpenProp } from 'types/common'
import SmsOptInModal from './sms-opt-in-modal'
import { SMS_PHONE_VERIFICATION_ENABLED } from '@/lib/smsVerification'

const notificationTypes = [
  {
    label:
      'How would you like to receive product and other updates from Culineer',
    updateType: 'CULINEER_UPDATES',
  },
  {
    label:
      'You will be notified when members, farmers, and markets you follow post an update, add a recipe or respond to your content. How would you like to receive these notifications?',
    updateType: 'FOLLOWING_UPDATES',
  },
]

const NON_SMS_CHANNELS = ['EMAIL', 'IN_APP']
const ALL_CHANNELS = ['EMAIL', 'SMS', 'IN_APP']

const ProfilePreference: React.FC<ProfileSettingsOpenProp> = ({
  handleProfileSettingsOpen,
}) => {
  const [initialValues, setInitialValues] = useState(null)
  const dispatch = useDispatch()
  const user = useSelector((state: RootState) => state.culineerAuth.user)
  const userID = user?.user_id

  // SMS opt-in modal state
  const [smsModalOpen, setSmsModalOpen] = useState(false)
  // Which updateType triggered the SMS opt-in (we enable SMS for that type on success)
  const [pendingSmsUpdateType, setPendingSmsUpdateType] = useState<string | null>(null)

  const [savePrefs, { loading: loadingSavePrefs }] = useUpsertNotificationPreferencesMutation()
  const [updateProfile] = useUpdateProfileMutation()

  const { data, loading, refetch } = useGetNotificationPreferencesQuery({
    fetchPolicy: 'no-cache',
    variables: { userID },
    skip: !userID,
  })

  useEffect(() => {
    if (data && userID) {
      const mapped = notificationTypes.map(({ updateType }) => {
        const prefChannels =
          data.getNotificationPreferences
            ?.filter((p) => p.updateType === updateType && p.isEnabled)
            .map((p) => p.channel) || []
        return { updateType, channels: prefChannels }
      })
      setInitialValues({ preferences: mapped })
    }
  }, [data, userID])

  if (!initialValues)
    return (
      <div className="flex justify-center items-center w-full">
        <p>Please wait...</p>
      </div>
    )

  // Whether the user currently has SMS enabled for any update type
  const smsCurrentlyEnabled = data?.getNotificationPreferences?.some(
    (p) => p.channel === 'SMS' && p.isEnabled
  ) ?? false

  const handleSmsToggle = async (
    index: number,
    updateType: string,
    currentlyChecked: boolean,
    setFieldValue: (field: string, value: any) => void,
    currentChannels: string[]
  ) => {
    if (currentlyChecked) {
      // User is turning SMS off for this specific update type only
      try {
        await savePrefs({
          variables: {
            input: [
              {
                userID,
                update_type: updateType,
                channel: 'SMS',
                isEnabled: false,
              },
            ],
          },
        })
        const updated = currentChannels.filter((c) => c !== 'SMS')
        setFieldValue(`preferences[${index}].channels`, updated)
        toast.success('SMS notifications disabled.')
        refetch()
      } catch (err: any) {
        toast.error(err.message || 'Could not disable SMS notifications.')
      }
    } else {
      // User is turning SMS on — open the opt-in modal
      setPendingSmsUpdateType(updateType)
      setSmsModalOpen(true)
    }
  }

  const handleSmsOptInSuccess = async (phone: string) => {
    if (!pendingSmsUpdateType) return
    try {
      // Phone verification is on hold (see lib/smsVerification.ts) — save the
      // number to the profile, but don't claim SMS is enabled since we never
      // actually verified it or confirmed Twilio can send to it.
      await updateProfile({ variables: { userID: userID as number, input: { phone } } })

      if (SMS_PHONE_VERIFICATION_ENABLED) {
        const payload = notificationTypes.flatMap(({ updateType }) => {
          const existing =
            data?.getNotificationPreferences?.filter((p) => p.updateType === updateType) || []
          return ALL_CHANNELS.map((channel) => {
            const existingPref = existing.find((p) => p.channel === channel)
            const isEnabled =
              channel === 'SMS'
                ? updateType === pendingSmsUpdateType // enable SMS only for the triggered type
                : existingPref?.isEnabled ?? false
            return {
              userID,
              update_type: updateType,
              channel,
              isEnabled,
            }
          })
        })

        await savePrefs({ variables: { input: payload } })
        dispatch(setUser({ communication_completed: true }))
        toast.success('SMS notifications enabled.')
      } else {
        toast.success('Phone number saved.')
      }
      refetch()
    } catch (err: any) {
      toast.error(err.message || 'Could not save phone number.')
    } finally {
      setPendingSmsUpdateType(null)
    }
  }

  return (
    <>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={async (values, { setSubmitting }) => {
          try {
            const payload = []

            values.preferences.forEach((pref) => {
              // Only save non-SMS channels through the normal form submit.
              // SMS is managed separately via the opt-in modal / DisableSmsNotifications.
              NON_SMS_CHANNELS.forEach((channel) => {
                payload.push({
                  userID,
                  update_type: pref.updateType,
                  channel,
                  isEnabled: pref.channels.includes(channel),
                })
              })
            })

            const prefRes = await savePrefs({ variables: { input: payload } })

            if (prefRes.data.upsertNotificationPreferences) {
              dispatch(setUser({ communication_completed: true }))
              toast.success('Preferences updated')
              setSubmitting(false)
            }
          } catch (error) {
            console.error('Error updating profile preferences:', error)
            toast.error(error.message)
          }
        }}
      >
        {({ values, setFieldValue, isSubmitting }) => (
          <Form>
            {notificationTypes.map((type, index) => (
              <React.Fragment key={type.updateType}>
                <div className="flex flex-col">
                  <div className="flex items-center gap-2 mb-2">
                    <Notification
                      size={20}
                      color="#85C34A"
                      variant="Bold"
                      className="flex-shrink-0 sm:block hidden"
                    />
                    <p className="text-base font-medium">{type.label}</p>
                  </div>
                  <div className="flex justify-between sm:items-center sm:flex-row flex-col items-start">
                    {ALL_CHANNELS.map((channel) => {
                      const isChecked = values?.preferences[index].channels.includes(channel)

                      if (channel === 'SMS') {
                        return (
                          <label key={channel} className="flex items-center gap-2">
                            <Checkbox
                              color="green"
                              className="checked:bg-[#85C349] checked:border-none checked:text-white"
                              checked={isChecked}
                              disabled={loadingSavePrefs}
                              onChange={() =>
                                handleSmsToggle(
                                  index,
                                  type.updateType,
                                  isChecked,
                                  setFieldValue,
                                  values.preferences[index].channels
                                )
                              }
                            />
                            <span className="flex items-center gap-1">
                              Text Messages
                              {smsCurrentlyEnabled && isChecked && (
                                <span className="ml-1 rounded-full bg-[#85C349]/10 px-2 py-0.5 text-xs font-medium text-[#85C349]">
                                  Active
                                </span>
                              )}
                            </span>
                          </label>
                        )
                      }

                      return (
                        <label key={channel} className="flex items-center gap-2">
                          <Field
                            type="checkbox"
                            name={`preferences[${index}].channels`}
                            value={channel}
                            as={Checkbox}
                            color="#85C349"
                            className="checked:bg-primary checked:border-none checked:text-white"
                            checked={isChecked}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                              const current = new Set(values.preferences[index].channels)
                              e.target.checked ? current.add(channel) : current.delete(channel)
                              setFieldValue(
                                `preferences[${index}].channels`,
                                Array.from(current)
                              )
                            }}
                          />
                          {channel === 'EMAIL' && 'Email Threads'}
                          {channel === 'IN_APP' && 'In-app Messages'}
                        </label>
                      )
                    })}
                  </div>
                </div>
                {index < notificationTypes.length - 1 && (
                  <div className="my-4 border-t border-gray-200" />
                )}
              </React.Fragment>
            ))}

            <div className="my-1">
              <p className="text-md text-gray-500">
                By selecting text or email I agree to receive messages from Culineer.
              </p>
            </div>

            <div className="flex items-center justify-end gap-4 pt-4">
              <button
                type="button"
                onClick={handleProfileSettingsOpen}
                className="px-4 py-2 rounded-full text-base font-medium"
              >
                Back
              </button>
              <Button
                className="w-28 rounded-full bg-[#85C349] capitalize text-base font-medium"
                type="submit"
                disabled={loadingSavePrefs || isSubmitting}
              >
                {loadingSavePrefs || isSubmitting ? (
                  <FontAwesomeIcon
                    icon={faSpinner}
                    className="h-6 w-6 animate-spin text-white"
                  />
                ) : (
                  'Save'
                )}
              </Button>
            </div>
          </Form>
        )}
      </Formik>

      {/* SMS opt-in modal — rendered outside the form to avoid nesting issues */}
      <SmsOptInModal
        open={smsModalOpen}
        onClose={() => {
          setSmsModalOpen(false)
          setPendingSmsUpdateType(null)
        }}
        onSuccess={handleSmsOptInSuccess}
        currentPhoneNumber={user?.phone_number}
      />
    </>
  )
}

export default ProfilePreference
